import styled from 'styled-components';

export const CardWrapper = styled.div<{ isLongPressing?: boolean }>`
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.02);
  }
  
  /* Long press visual feedback */
  ${props => props.isLongPressing && `
    transform: scale(0.98);
    filter: brightness(0.95);
  `}
  
  /* Touch-friendly sizing on mobile */
  @media (max-width: 768px) {
    /* Ensure minimum touch target size */
    min-height: 44px;
    
    /* Add subtle indication that card is touch-interactive */
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

export const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${CardWrapper}:hover & {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border-color: rgba(31, 162, 255, 0.2);
  }
  
  /* Add subtle click indicator when no actions menu */
  &::after {
    content: '';
    position: absolute;
    top: 16px;
    right: 16px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
    opacity: 0.6;
    transition: all 0.3s ease;
  }
  
  ${CardWrapper}:hover &::after {
    opacity: 1;
    transform: scale(1.2);
  }
  
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

export const ClassTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.3;
  flex: 1;
  padding-right: 12px;
`;

export const ClassIcon = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

export const ActionsContainer = styled.div`
  position: relative;
`;

export const MobileLongPressHint = styled.div`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  white-space: nowrap;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  ${CardWrapper}:hover & {
    opacity: 1;
  }
`;

// Hide click indicator when actions container is present
export const CardWithActions = styled(Card)`
  &::after {
    display: none;
  }
`;

export const ActionsButton = styled.button`
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #1a1a1a;
  font-size: 18px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: rgba(31, 162, 255, 0.1);
    border-color: rgba(31, 162, 255, 0.2);
    color: #1677ff;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

export const ActionsMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 8px 0;
  min-width: 140px;
  z-index: 1000;
  animation: menuSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes menuSlideIn {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const MenuItem = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
  color: #1a1a1a;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(31, 162, 255, 0.08);
  }
  
  .anticon {
    font-size: 14px;
    color: #666;
  }
`;

export const MenuItemDanger = styled(MenuItem)`
  color: #ff4d4f;
  
  &:hover {
    background: rgba(255, 77, 79, 0.08);
  }
  
  .anticon {
    color: #ff4d4f;
  }
`;

export const StatsSection = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
`;

export const GradeStatIcon = styled(StatIcon)`
  background: #fa8c16 !important;
`;

export const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StatValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;
`;

export const StatLabel = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


// Mobile responsive styles
export const MobileCard = styled(Card)`
  @media (max-width: 768px) {
    padding: 20px;
    
    ${ClassTitle} {
      font-size: 16px;
    }
    
    ${StatsSection} {
      gap: 16px;
      margin-bottom: 12px;
    }
    
    ${StatIcon} {
      width: 36px;
      height: 36px;
      font-size: 14px;
    }
    
    ${StatValue} {
      font-size: 16px;
    }
    
    ${StatLabel} {
      font-size: 11px;
    }
    
  }
`;
